import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getDatabase,
  get,
  onValue,
  push,
  ref,
  remove,
  update,
} from "firebase/database";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PointWarning from "./components/PointWarning";
import ScheduleModeSelector from "./components/ScheduleModeSelector";
import FixedDateScheduleUI from "./components/FixedDateScheduleUI";
import SettingTooltip, {
  CONTACT_INFO_TOOLTIP_TEXT,
} from "./components/SettingTooltip";
import * as XLSX from "xlsx";
import {
  getSocialAccountKey,
  loadConnectedSocialAccounts,
  toLegacyFacebookFanpagePayload,
  toSelectedAccountPayload,
} from "./utils/socialChannels";
import { FaFacebook, FaUpload, FaSpinner, FaFilm, FaImage } from "react-icons/fa";
import zaloIcon from "./images/zalo.png";
import axios from "axios";

const DEFAULT_DAYS = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: true,
  sunday: true,
};

const DAY_OPTIONS = [
  { key: "monday", label: "Thứ 2" },
  { key: "tuesday", label: "Thứ 3" },
  { key: "wednesday", label: "Thứ 4" },
  { key: "thursday", label: "Thứ 5" },
  { key: "friday", label: "Thứ 6" },
  { key: "saturday", label: "Thứ 7" },
  { key: "sunday", label: "Chủ nhật" },
];

// Thêm daysInVietnamese object
const daysInVietnamese = {
  monday: "Thứ 2",
  tuesday: "Thứ 3",
  wednesday: "Thứ 4",
  thursday: "Thứ 5",
  friday: "Thứ 6",
  saturday: "Thứ 7",
  sunday: "Chủ nhật",
};

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700",
  in_progress: "bg-sky-100 text-sky-700",
  posted: "bg-emerald-100 text-emerald-700",
  failed: "bg-rose-100 text-rose-700",
};

const MEDIA_TYPE_OPTIONS = [
  { value: "none", label: "Chỉ văn bản" },
  { value: "image", label: "Ảnh" },
  { value: "video", label: "Video" },
];

const createQueueItem = (overrides = {}) => {
  const now = Date.now();
  return {
    id: `manual-${now}-${Math.random().toString(36).slice(2, 8)}`,
    order: 0,
    content: "",
    mediaType: "none",
    mediaUrls: "",
    status: "pending",
    attempts: 0,
    fanpageResults: {},
    createdAt: now,
    updatedAt: now,
    lastAttemptAt: null,
    postedAt: null,
    ...overrides,
  };
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "—";
  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("vi-VN", {
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    console.error("Failed to format timestamp", error);
    return "—";
  }
};

const parseHistoryLinks = (links = []) => {
  return links
    .map((entry) => {
      if (typeof entry !== "string") return null;
      const [timestamp, link] = entry.split("|");
      const ts = Number(timestamp);
      if (!link || Number.isNaN(ts)) return null;
      return { timestamp: ts, link };
    })
    .filter(Boolean)
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 10);
};

// Danh sách các pattern URL không hỗ trợ (không phải direct media URLs)
const UNSUPPORTED_URL_PATTERNS = [
  { pattern: /photos\.google\.com/i, name: "Google Photos", suggestion: "Hãy mở ảnh và copy link trực tiếp từ ảnh (link kết thúc bằng .jpg, .png, etc.)" },
  { pattern: /drive\.google\.com/i, name: "Google Drive", suggestion: "Hãy sử dụng link chia sẻ trực tiếp hoặc link tải xuống từ Google Drive" },
  { pattern: /dropbox\.com(?!.*\?dl=1)/i, name: "Dropbox", suggestion: "Hãy thêm ?dl=1 vào cuối link Dropbox để lấy link tải trực tiếp" },
  { pattern: /instagram\.com/i, name: "Instagram", suggestion: "Facebook không hỗ trợ link Instagram. Hãy tải ảnh về và upload lên hosting khác" },
  { pattern: /pinterest\.com/i, name: "Pinterest", suggestion: "Hãy tải ảnh về và upload lên hosting khác" },
];

// Function kiểm tra URL có phải là direct media URL không
const checkUnsupportedUrls = (urls) => {
  const warnings = [];
  
  for (const url of urls) {
    // Kiểm tra các pattern không hỗ trợ
    for (const { pattern, name, suggestion } of UNSUPPORTED_URL_PATTERNS) {
      if (pattern.test(url)) {
        warnings.push({
          url,
          type: "unsupported_source",
          message: `Link ${name} không phải là direct image URL và có thể không hoạt động với Facebook.`,
          suggestion,
        });
        break;
      }
    }
    
    // Kiểm tra xem URL có chứa ký tự pipe (|) được encode không - thường là dấu hiệu của URL không hợp lệ
    if (url.includes("%7C") || url.includes("|")) {
      warnings.push({
        url,
        type: "special_characters",
        message: "URL chứa ký tự đặc biệt (|) có thể gây lỗi khi đăng bài.",
        suggestion: "Hãy kiểm tra lại URL hoặc sử dụng link khác",
      });
    }
    
    // Kiểm tra xem URL có phải là direct image/video URL không (kết thúc bằng extension)
    const isDirectMediaUrl = /\.(jpg|jpeg|png|gif|webp|bmp|mp4|avi|mov|wmv|flv|mkv|webm|ogv|m4v)(\?.*)?$/i.test(url);
    const isKnownHosting = /(cloudinary\.com|imgur\.com|fbcdn\.net|amazonaws\.com|storage\.googleapis\.com|firebasestorage\.googleapis\.com)/i.test(url);
    
    if (!isDirectMediaUrl && !isKnownHosting && !warnings.some(w => w.url === url)) {
      // Chỉ warning nếu không phải direct URL và không phải hosting đã biết
      const hasMediaKeyword = url.toLowerCase().includes("image") || url.toLowerCase().includes("photo") || url.toLowerCase().includes("video");
      if (hasMediaKeyword) {
        warnings.push({
          url,
          type: "indirect_url",
          message: "URL có vẻ không phải là link trực tiếp đến file media.",
          suggestion: "Để đảm bảo bài viết đăng thành công, hãy sử dụng link trực tiếp kết thúc bằng .jpg, .png, .mp4, etc.",
        });
      }
    }
  }
  
  return warnings;
};

// Thêm function để validate real-time media URLs
const validateMediaUrlsRealtime = (urls) => {
  // Kiểm tra URLs không hỗ trợ trước
  const unsupportedWarnings = checkUnsupportedUrls(urls);
  if (unsupportedWarnings.length > 0) {
    const firstWarning = unsupportedWarnings[0];
    return `⚠️ ${firstWarning.message}\n${firstWarning.suggestion}`;
  }

  if (urls.length <= 1) return null; // Không cần validate số lượng nếu chỉ có 1 URL hoặc ít hơn

  const videoUrls = urls.filter(
    (url) =>
      /\.(mp4|avi|mov|wmv|flv|mkv|webm|ogv|m4v)$/i.test(url) ||
      /(youtube\.com|youtu\.be|vimeo\.com|facebook\.com.*video|fb\.watch)/i.test(
        url
      )
  );

  const imageUrls = urls.filter(
    (url) =>
      /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(url) ||
      (!videoUrls.includes(url) &&
        (url.toLowerCase().includes("image") ||
          url.toLowerCase().includes("photo")))
  );

  if (videoUrls.length > 1) {
    return "Bài đăng chỉ hỗ trợ một video duy nhất. Vui lòng kiểm tra lại.";
  }

  // Facebook API không cho phép đăng cả video và ảnh cùng lúc
  if (videoUrls.length > 0 && imageUrls.length > 0) {
    return "Facebook không cho phép đăng cả video và ảnh trong cùng một bài viết. Vui lòng chọn chỉ video hoặc chỉ ảnh.";
  }

  return null; // Không có lỗi
};

const ManualPostGenerator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const configFromState = location.state?.config || null;
  const configIdFromQuery = searchParams.get("id");

  const [configId, setConfigId] = useState(
    configFromState?.id || configIdFromQuery || null
  );
  const [configName, setConfigName] = useState(configFromState?.name || "");
  const [postQueue, setPostQueue] = useState(() => [createQueueItem()]);
  const [fanpages, setFanpages] = useState([]);
  const [selectedFanpages, setSelectedFanpages] = useState(
    configFromState?.selectedAccounts || configFromState?.selectedFanpages || []
  );
  const [scheduleMode, setScheduleMode] = useState(
    configFromState?.scheduleMode || "weekly"
  );
  const [selectedDays, setSelectedDays] = useState(
    configFromState?.selectedDays || DEFAULT_DAYS
  );
  const [selectedTimes, setSelectedTimes] = useState(
    configFromState?.times && configFromState?.times.length > 0
      ? configFromState.times
      : ["08:00"]
  );
  const [fixedDateSchedules, setFixedDateSchedules] = useState(
    configFromState?.fixedDateSchedules || []
  );
  const [timeInput, setTimeInput] = useState("08:00");
  const [autoPostEnabled, setAutoPostEnabled] = useState(
    configFromState?.autoPostEnabled ?? true
  );
  const [includeContactInfo, setIncludeContactInfo] = useState(
    configFromState?.includeContactInfo ?? true
  );
  const [repeatQueue, setRepeatQueue] = useState(
    configFromState?.rotation?.repeatQueue ?? false
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(
    !!configId && !configFromState
  );
  const [historyLinks, setHistoryLinks] = useState([]);
  const [metrics, setMetrics] = useState(configFromState?.metrics || null);
  const [userPoint, setUserPoint] = useState(0);
  const [mediaValidationErrors, setMediaValidationErrors] = useState({}); // Thêm state cho validation errors
  // States cho UI tối ưu danh sách bài viết
  const [expandedPosts, setExpandedPosts] = useState(new Set());

  // States cho Excel import
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  // States cho upload media lên Cloudinary
  const [uploadingPosts, setUploadingPosts] = useState({});

  // Ref cho dropdown để xử lý click outside
  const dropdownRef = useRef(null);

  // Helper functions cho UI tối ưu
  const togglePostExpansion = (id) => {
    const newExpanded = new Set(expandedPosts);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedPosts(newExpanded);
  };

  const uploadFileToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "AutoWork");
    formData.append("api_key", "238276161954221");

    const isVideo = file.type.startsWith("video/");
    const fileType = isVideo ? "video" : "image";
    const uploadUrl = `https://api.cloudinary.com/v1_1/test-upload-1/${fileType}/upload`;

    try {
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return {
        url: response.data.secure_url,
        type: fileType,
      };
    } catch (error) {
      console.error(`Failed to upload ${fileType} to Cloudinary:`, error);
      throw error;
    }
  };

  const handleMediaUpload = async (itemId, event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const isVideo = file.type.startsWith("video/");
    
    // Giới hạn dung lượng: Video < 100MB, Ảnh < 10MB
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Kích thước file quá lớn. Tối đa là ${isVideo ? "100MB" : "10MB"}.`);
      return;
    }

    setUploadingPosts((prev) => ({ ...prev, [itemId]: true }));

    try {
      const uploaded = await uploadFileToCloudinary(file);
      
      setPostQueue((prevQueue) =>
        prevQueue.map((item) => {
          if (item.id === itemId) {
            const currentUrls = item.mediaUrls
              .split(/\n|,/)
              .map((url) => url.trim())
              .filter(Boolean);
            
            const newUrls = [...currentUrls, uploaded.url];
            
            return {
              ...item,
              mediaType: isVideo ? "video" : "image",
              mediaUrls: newUrls.join("\n"),
              updatedAt: Date.now(),
            };
          }
          return item;
        })
      );
      
      // Trigger validation real-time
      setTimeout(() => {
        setPostQueue((currentQueue) => {
          const updatedPost = currentQueue.find((p) => p.id === itemId);
          if (updatedPost) {
            const currentUrls = updatedPost.mediaUrls
              .split(/\n|,/)
              .map((url) => url.trim())
              .filter(Boolean);
            const validationError = validateMediaUrlsRealtime(currentUrls);
            setMediaValidationErrors((prev) => ({
              ...prev,
              [itemId]: validationError,
            }));
          }
          return currentQueue;
        });
      }, 100);

      toast.success("Tải tệp lên thành công!");
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Tải tệp lên thất bại. Vui lòng thử lại.");
    } finally {
      setUploadingPosts((prev) => ({ ...prev, [itemId]: false }));
      event.target.value = "";
    }
  };

  const fetchUserPoints = async () => {
    try {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");
      // Cập nhật đường dẫn để lấy điểm từ sub-collection riêng
      const pointsRef = ref(db, `users/${userId}/points`);
      const snapshot = await get(pointsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        if (data.points !== undefined && data.purchasedPoints !== undefined) {
          // Cấu trúc mới
          setUserPoint(data.points + data.purchasedPoints);
        } else if (typeof data.points === "number") {
          // Cấu trúc cũ
          setUserPoint(data.points);
        } else {
          console.error("Invalid points value:", data);
          setUserPoint(0);
        }
      } else {
        setUserPoint(0);
      }
    } catch (error) {
      console.error("Error fetching user points:", error);
      setUserPoint(0);
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      navigate("/");
      return;
    }

    fetchUserPoints();

    const db = getDatabase();

    loadConnectedSocialAccounts(db, userId)
      .then((fanpageList) => {
        setFanpages(fanpageList);

        if (!configFromState?.selectedFanpages && fanpageList.length === 1) {
          setSelectedFanpages([fanpageList[0]]);
        }
      })
      .catch((error) => {
        console.error("Failed to load posting accounts", error);
        toast.error("Không thể tải danh sách tài khoản. Vui lòng thử lại sau.");
      });
  }, [configFromState, navigate]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId || !configId) return;

    const db = getDatabase();
    const historyRef = ref(
      db,
      `users/${userId}/posts/manualPostGenerator/configs/${configId}/links`
    );

    const unsubscribe = onValue(historyRef, (snapshot) => {
      const links = snapshot.val() || [];
      setHistoryLinks(parseHistoryLinks(links));
    });

    return () => unsubscribe();
  }, [configId]);

  useEffect(() => {
    if (!configFromState && configIdFromQuery) {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const configRef = ref(db, `manualPosts/${userId}/${configIdFromQuery}`);
      setIsLoadingConfig(true);
      get(configRef)
        .then((snapshot) => {
          if (!snapshot.exists()) {
            toast.error("Không tìm thấy thiết lập thủ công.");
            setIsLoadingConfig(false);
            return;
          }
          const data = snapshot.val();
          applyConfigFromDatabase(data, configIdFromQuery);
        })
        .catch((error) => {
          console.error("Failed to load manual config", error);
          toast.error("Không thể tải thiết lập thủ công.");
        })
        .finally(() => setIsLoadingConfig(false));
    } else if (configFromState) {
      applyConfigFromDatabase(configFromState, configFromState.id || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configFromState, configIdFromQuery]);

  // useEffect để tự động expand bài viết đầu tiên khi tạo mới thiết lập
  useEffect(() => {
    // Kiểm tra xem có phải chế độ tạo mới không (không có config từ state và không có configId từ query)
    const isNewConfig = !configFromState && !configIdFromQuery;

    if (isNewConfig && postQueue.length > 0) {
      // Tự động expand bài viết đầu tiên
      const firstPostId = postQueue[0].id;
      setExpandedPosts((prevExpanded) => {
        const newExpanded = new Set(prevExpanded);
        newExpanded.add(firstPostId);
        return newExpanded;
      });
    }
  }, [configFromState, configIdFromQuery, postQueue]);

  // Đồng bộ access_token từ danh sách fanpages của người dùng vào selectedFanpages khi load cấu hình cũ
  useEffect(() => {
    if (fanpages.length > 0 && selectedFanpages.length > 0) {
      const hasMissingToken = selectedFanpages.some(
        (fp) => (fp.platform || "facebook") === "facebook" && !fp.access_token
      );
      if (hasMissingToken) {
        setSelectedFanpages((prev) =>
          prev.map((selectedFp) => {
            if ((selectedFp.platform || "facebook") === "facebook" && !selectedFp.access_token) {
              const matched = fanpages.find(
                (fp) => getSocialAccountKey(fp) === getSocialAccountKey(selectedFp)
              );
              if (matched) {
                return {
                  ...selectedFp,
                  ...matched, // Đồng bộ thông tin đầy đủ bao gồm cả access_token mới nhất của người dùng
                };
              }
            }
            return selectedFp;
          })
        );
      }
    }
  }, [fanpages, selectedFanpages]);

  // useEffect để xử lý click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const applyConfigFromDatabase = (configData, id) => {
    if (!configData) return;
    setConfigId(id || null);
    setConfigName(configData.name || "");
    setSelectedFanpages(
      configData.selectedAccounts || configData.selectedFanpages || []
    );

    // Load schedule mode with backward compatibility
    const loadedScheduleMode = configData.scheduleMode || "weekly";
    setScheduleMode(loadedScheduleMode);

    setSelectedDays({ ...DEFAULT_DAYS, ...(configData.selectedDays || {}) });
    setSelectedTimes(
      configData.times &&
        Array.isArray(configData.times) &&
        configData.times.length
        ? configData.times
        : ["08:00"]
    );
    setFixedDateSchedules(configData.fixedDateSchedules || []);
    setTimeInput(
      configData.times &&
        Array.isArray(configData.times) &&
        configData.times.length
        ? configData.times[0]
        : "08:00"
    );
    setAutoPostEnabled(configData.autoPostEnabled !== false);
    setIncludeContactInfo(configData.includeContactInfo ?? true);
    setRepeatQueue(configData.rotation?.repeatQueue ?? false);
    setMetrics(configData.metrics || null);

    const queueObject = configData.postQueue || {};
    const queueArray = Array.isArray(queueObject)
      ? queueObject
      : Object.values(queueObject);

    if (queueArray.length === 0) {
      setPostQueue([createQueueItem()]);
      return;
    }

    const sortedQueue = queueArray
      .map((item, index) => {
        const mediaUrls = Array.isArray(item?.media?.urls)
          ? item.media.urls.join("\n")
          : Array.isArray(item?.imageURLs)
          ? item.imageURLs.join("\n")
          : item.mediaUrls || "";

        const mediaType = item?.media?.type
          ? item.media.type
          : item.mediaType || (mediaUrls ? "image" : "none");

        const order = typeof item.order === "number" ? item.order : index;

        return createQueueItem({
          id: item.id || `queue-${index}`,
          order,
          content: item.content || item.text || "",
          mediaType,
          mediaUrls,
          status: item.status || "pending",
          attempts: item.attempts || 0,
          fanpageResults: item.fanpageResults || {},
          createdAt: item.createdAt || Date.now(),
          updatedAt: item.updatedAt || Date.now(),
          lastAttemptAt: item.lastAttemptAt || null,
          postedAt: item.postedAt || null,
        });
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    setPostQueue(sortedQueue);
  };

  const queueStats = useMemo(() => {
    const totals = {
      total: postQueue.length,
      pending: 0,
      in_progress: 0,
      posted: 0,
      failed: 0,
    };

    postQueue.forEach((item) => {
      const status = item.status || "pending";
      if (totals[status] !== undefined) {
        totals[status] += 1;
      }
    });

    return totals;
  }, [postQueue]);

  const handleAddPost = () => {
    // Kiểm tra xem có bài viết nào không có nội dung không
    const hasEmptyPost = postQueue.some(
      (item) => !item.content || !item.content.trim()
    );

    if (hasEmptyPost) {
      toast.warn(
        "Vui lòng hoàn thành nội dung các bài viết hiện tại trước khi thêm bài mới."
      );
      return;
    }

    setPostQueue((prev) => {
      const newOrder =
        prev.length > 0
          ? Math.max(...prev.map((item) => item.order || 0)) + 1
          : 0;
      const newItem = createQueueItem({ order: newOrder });
      const updatedQueue = [...prev, newItem];

      // Tự động expand bài viết mới được thêm vào
      setExpandedPosts((prevExpanded) => {
        const newExpanded = new Set(prevExpanded);
        newExpanded.add(newItem.id);
        return newExpanded;
      });

      return updatedQueue;
    });
  };

  const handleDuplicatePost = (index) => {
    setPostQueue((prev) => {
      const item = prev[index];
      const duplicated = createQueueItem({
        content: item.content,
        mediaType: item.mediaType,
        mediaUrls: item.mediaUrls,
        order: item.order + 0.5, // Tạo order trung gian
      });
      const newQueue = [...prev];
      newQueue.splice(index + 1, 0, duplicated);

      // Cập nhật lại order cho tất cả items để đảm bảo thứ tự đúng
      const updatedQueue = newQueue.map((queueItem, idx) => ({
        ...queueItem,
        order: idx,
      }));

      return updatedQueue;
    });
  };

  const handleDeletePost = (index) => {
    if (postQueue.length === 1) {
      toast.warn("Cần ít nhất 1 bài trong hàng chờ.");
      return;
    }
    setPostQueue((prev) => {
      const newQueue = prev.filter((_, i) => i !== index);
      // Cập nhật lại order cho tất cả items sau khi xóa
      const updatedQueue = newQueue.map((queueItem, idx) => ({
        ...queueItem,
        order: idx,
      }));

      return updatedQueue;
    });
  };

  const movePost = (index, direction) => {
    setPostQueue((prev) => {
      const newQueue = [...prev];
      const swapIndex = index + direction;
      if (swapIndex < 0 || swapIndex >= newQueue.length) return prev;

      // Hoán đổi vị trí
      [newQueue[index], newQueue[swapIndex]] = [
        newQueue[swapIndex],
        newQueue[index],
      ];

      // Cập nhật lại order cho tất cả items để đảm bảo thứ tự đúng
      const updatedQueue = newQueue.map((queueItem, idx) => ({
        ...queueItem,
        order: idx,
      }));

      return updatedQueue;
    });
  };

  const updateQueueItem = (id, updates) => {
    setPostQueue((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              ...updates,
              updatedAt: Date.now(),
            }
          : item
      )
    );

    // Nếu update mediaUrls, thực hiện validation real-time
    if (updates.mediaUrls !== undefined) {
      const urls = updates.mediaUrls
        .split(/\n|,/)
        .map((url) => url.trim())
        .filter(Boolean);

      const validationError = validateMediaUrlsRealtime(urls);

      setMediaValidationErrors((prev) => ({
        ...prev,
        [id]: validationError,
      }));
    }
  };

  // Function để xử lý mở template Excel trên Google Sheets
  const handleDownloadTemplate = () => {
    const templateUrl =
      "https://docs.google.com/spreadsheets/d/1vlvDr8bdcKknF1p1jU5YanmSJuwP7RR7jCqGodYViqU/edit?usp=sharing";
    window.open(templateUrl, "_blank");
  };

  // Function để xử lý import Excel
  const handleExcelImport = async (file) => {
    if (!file) return;

    // Kiểm tra định dạng file
    const allowedTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (
      !allowedTypes.includes(file.type) &&
      !file.name.match(/\.(xlsx?|csv)$/i)
    ) {
      toast.error("Vui lòng chọn file Excel (.xlsx, .xls) hoặc CSV.");
      return;
    }

    setIsImporting(true);
    setImportProgress(0);

    try {
      const data = await file.arrayBuffer();
      setImportProgress(25);

      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      setImportProgress(50);

      // Chuyển đổi sheet thành JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length < 2) {
        toast.error("File Excel phải có ít nhất 2 dòng (header và dữ liệu).");
        return;
      }

      setImportProgress(75);

      // Bỏ qua dòng header (dòng đầu tiên)
      const dataRows = jsonData.slice(1);
      const importedPosts = [];

      dataRows.forEach((row, index) => {
        // Kiểm tra dòng có dữ liệu không
        if (!row || row.length < 2) return;

        const [stt, content, mediaUrls] = row;

        // Bỏ qua dòng trống hoặc không có nội dung
        if (!content || !content.toString().trim()) return;

        // Xử lý media URLs
        let processedMediaUrls = "";
        let mediaType = "none";

        if (mediaUrls && mediaUrls.toString().trim()) {
          // Tách các URL bằng dấu ; và chuyển thành xuống dòng
          processedMediaUrls = mediaUrls
            .toString()
            .split(";")
            .map((url) => url.trim())
            .filter((url) => url)
            .join("\n");

          if (processedMediaUrls) {
            // Tự động detect media type
            const urls = processedMediaUrls.split("\n");
            const hasVideo = urls.some(
              (url) =>
                /\.(mp4|avi|mov|wmv|flv|mkv|webm|ogv|m4v)$/i.test(url) ||
                /(youtube\.com|youtu\.be|vimeo\.com|facebook\.com.*video|fb\.watch)/i.test(
                  url
                )
            );
            mediaType = hasVideo ? "video" : "image";
          }
        }

        const newPost = createQueueItem({
          content: content.toString().trim(),
          mediaType,
          mediaUrls: processedMediaUrls,
          order: postQueue.length + importedPosts.length,
        });

        importedPosts.push(newPost);
      });

      if (importedPosts.length === 0) {
        toast.warn("Không tìm thấy dữ liệu hợp lệ trong file Excel.");
        return;
      }

      // Thêm các bài viết đã import vào queue
      setPostQueue((prev) => {
        // Nếu queue hiện tại chỉ có 1 item rỗng, thay thế nó
        if (prev.length === 1 && !prev[0].content.trim()) {
          const updatedQueue = importedPosts.map((post, index) => ({
            ...post,
            order: index,
          }));
          return updatedQueue;
        } else {
          // Thêm vào cuối queue
          const updatedQueue = [...prev, ...importedPosts].map(
            (post, index) => ({
              ...post,
              order: index,
            })
          );
          return updatedQueue;
        }
      });

      setImportProgress(100);
      toast.success(
        `Đã import thành công ${importedPosts.length} bài viết từ Excel.`
      );
    } catch (error) {
      console.error("Error importing Excel:", error);
      toast.error(
        "Có lỗi xảy ra khi đọc file Excel. Vui lòng kiểm tra định dạng file."
      );
    } finally {
      setIsImporting(false);
      setImportProgress(0);
    }
  };

  const handleToggleFanpage = (fanpage) => {
    setSelectedFanpages((prev) => {
      const key = getSocialAccountKey(fanpage);
      const exists = prev.some((fp) => getSocialAccountKey(fp) === key);
      if (exists) {
        return prev.filter((fp) => getSocialAccountKey(fp) !== key);
      }
      return [...prev, fanpage];
    });
  };

  const handleToggleDay = (day) => {
    setSelectedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleAddTime = () => {
    if (!timeInput) return;
    if (selectedTimes.includes(timeInput)) {
      toast.info("Khung giờ đã tồn tại trong lịch.");
      return;
    }
    setSelectedTimes((prev) => [...prev, timeInput].sort());
  };

  const handleRemoveTime = (time) => {
    setSelectedTimes((prev) => prev.filter((item) => item !== time));
  };

  const handleAddFixedDateSchedule = (schedule) => {
    const newSchedule = {
      ...schedule,
      id: `schedule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      completed: false,
    };
    setFixedDateSchedules([...fixedDateSchedules, newSchedule]);
    toast.success("Đã thêm lịch đăng mới!");
  };

  const handleEditFixedDateSchedule = (index, updates) => {
    const updated = [...fixedDateSchedules];
    updated[index] = { ...updated[index], ...updates };
    setFixedDateSchedules(updated);
    toast.success("Đã cập nhật lịch đăng!");
  };

  const handleDeleteFixedDateSchedule = (index) => {
    setFixedDateSchedules(fixedDateSchedules.filter((_, i) => i !== index));
    toast.success("Đã xóa lịch đăng!");
  };

  const handleToggleScheduleCompleted = (index) => {
    const updated = [...fixedDateSchedules];
    updated[index] = {
      ...updated[index],
      completed: !updated[index].completed,
      completedAt: !updated[index].completed ? new Date().toISOString() : null,
    };
    setFixedDateSchedules(updated);
  };

  const validateBeforeSave = () => {
    if (!configName.trim()) {
      toast.error("Vui lòng nhập tên thiết lập.");
      return false;
    }

    if (selectedFanpages.length === 0) {
      toast.error("Vui lòng chọn ít nhất một tài khoản đăng bài.");
      return false;
    }

    const fanpagesMissingToken = selectedFanpages.filter(
      (fp) => (fp.platform || "facebook") === "facebook" && !fp.access_token
    );
    if (fanpagesMissingToken.length > 0) {
      toast.error(
        `Fanpage thiếu quyền truy cập: ${fanpagesMissingToken
          .map((fp) => fp.name)
          .join(", ")}`
      );
      return false;
    }

    // Validate schedule mode specific fields
    if (scheduleMode === "weekly") {
      if (!selectedTimes.length) {
        toast.error("Vui lòng thêm ít nhất một khung giờ đăng.");
        return false;
      }

      const selectedDayCount =
        Object.values(selectedDays).filter(Boolean).length;
      if (selectedDayCount === 0) {
        toast.error("Vui lòng chọn ít nhất một ngày đăng.");
        return false;
      }
    }

    if (scheduleMode === "fixed-dates") {
      if (fixedDateSchedules.length === 0) {
        toast.error("Vui lòng thêm ít nhất một lịch đăng!");
        return false;
      }

      // Validate each schedule
      for (const schedule of fixedDateSchedules) {
        if (!schedule.date) {
          toast.error("Mỗi lịch đăng phải có ngày.");
          return false;
        }

        if (!schedule.times || schedule.times.length === 0) {
          toast.error("Mỗi lịch đăng phải có ít nhất một giờ.");
          return false;
        }
      }
    }

    for (const item of postQueue) {
      if (!item.content || !item.content.trim()) {
        toast.error("Tất cả bài viết trong hàng chờ cần có nội dung.");
        return false;
      }

      // Check media URLs and validate mixed media restrictions
      const urls = item.mediaUrls
        .split(/\n|,/) // accept newline or comma separated
        .map((url) => url.trim())
        .filter(Boolean);

      const videoUrls = urls.filter(
        (url) =>
          /\.(mp4|avi|mov|wmv|flv|mkv|webm|ogv|m4v)$/i.test(url) ||
          /(youtube\.com|youtu\.be|vimeo\.com|facebook\.com.*video|fb\.watch)/i.test(
            url
          )
      );

      const imageUrls = urls.filter(
        (url) =>
          /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(url) ||
          (!videoUrls.includes(url) &&
            (url.toLowerCase().includes("image") ||
              url.toLowerCase().includes("photo")))
      );

      if (videoUrls.length > 1) {
        toast.error(
          "Bài đăng chỉ hỗ trợ một video duy nhất. Vui lòng kiểm tra lại."
        );
        return false;
      }

      const hasOnlyZaloAccounts = selectedFanpages.every(
        (account) => (account.platform || "facebook") === "zalo"
      );

      if (!hasOnlyZaloAccounts && videoUrls.length > 0 && imageUrls.length > 0) {
        toast.error(
          "Facebook không cho phép đăng cả video và ảnh trong cùng một bài viết. Vui lòng chọn chỉ video hoặc chỉ ảnh, hoặc chỉ chọn Zalo OA."
        );
        return false;
      }

      // Zalo Article bắt buộc có ảnh cover – không hỗ trợ bài viết chỉ văn bản
      if (hasOnlyZaloAccounts && urls.length === 0) {
        toast.error(
          "Zalo OA yêu cầu ít nhất 1 ảnh cho mỗi bài viết. Vui lòng thêm ảnh hoặc chọn thêm tài khoản Facebook."
        );
        return false;
      }
    }

    return true;
  };

  const buildQueuePayload = () => {
    const now = Date.now();
    const queuePayload = {};

    postQueue.forEach((item, index) => {
      const rawUrls = item.mediaUrls
        .split(/\n|,/) // newline or comma separated
        .map((url) => url.trim())
        .filter(Boolean);

      // Auto-detect media type based on URLs
      let mediaType = "none";
      if (rawUrls.length > 0) {
        const hasVideo = rawUrls.some(
          (url) =>
            /\.(mp4|avi|mov|wmv|flv|mkv|webm|ogv|m4v)$/i.test(url) ||
            /(youtube\.com|youtu\.be|vimeo\.com|facebook\.com.*video|fb\.watch)/i.test(
              url
            )
        );
        mediaType = hasVideo ? "video" : "image";
      }

      queuePayload[item.id] = {
        id: item.id,
        order: item.order ?? index, // Sử dụng item.order nếu có, fallback về index
        content: item.content.trim(),
        media: {
          type: mediaType,
          urls: rawUrls,
        },
        status: item.status || "pending",
        fanpageResults: item.fanpageResults || {},
        attempts: item.attempts || 0,
        createdAt: item.createdAt || now,
        updatedAt: now,
        lastAttemptAt: item.lastAttemptAt || null,
        postedAt: item.postedAt || null,
      };
    });

    return queuePayload;
  };

  const handleSaveConfig = async () => {
    if (isSaving) return;
    if (!validateBeforeSave()) return;

    const userId = localStorage.getItem("userId");
    if (!userId) {
      toast.error("Không tìm thấy người dùng. Vui lòng đăng nhập lại.");
      return;
    }

    setIsSaving(true);
    const db = getDatabase();
    const now = Date.now();

    const payload = {
      name: configName.trim(),
      autoPostEnabled,
      selectedAccounts: selectedFanpages.map(toSelectedAccountPayload),
      selectedFanpages: selectedFanpages
        .filter((account) => (account.platform || "facebook") === "facebook")
        .map(toLegacyFacebookFanpagePayload),
      scheduleMode: scheduleMode || "weekly",
      ...(scheduleMode === "weekly" && {
        selectedDays,
        times: selectedTimes,
      }),
      ...(scheduleMode === "fixed-dates" && {
        fixedDateSchedules,
      }),
      includeContactInfo,
      rotation: { repeatQueue },
      postQueue: buildQueuePayload(),
      metrics: metrics || { posted: 0, failed: 0 },
      updatedAt: now,
    };

    try {
      if (configId) {
        const configRef = ref(db, `manualPosts/${userId}/${configId}`);
        await update(configRef, payload);
        toast.success("Thiết lập thủ công đã được cập nhật.");
      } else {
        const listRef = ref(db, `manualPosts/${userId}`);
        const newRef = await push(listRef, {
          ...payload,
          createdAt: now,
        });
        setConfigId(newRef.key);
        toast.success("Đã tạo thiết lập đăng bài thủ công mới.");
      }
    } catch (error) {
      console.error("Failed to save manual config", error);
      toast.error("Không thể lưu thiết lập. Vui lòng thử lại.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfig = async () => {
    if (!configId || isDeleting) return;

    const confirmed = window.confirm(
      "Bạn có chắc muốn xóa thiết lập đăng bài thủ công này?"
    );
    if (!confirmed) return;

    const userId = localStorage.getItem("userId");
    if (!userId) return;

    setIsDeleting(true);
    const db = getDatabase();
    const configRef = ref(db, `manualPosts/${userId}/${configId}`);

    try {
      await remove(configRef);
      toast.success("Đã xóa thiết lập thủ công.");
      setTimeout(() => {
        navigate("/manual-post/configs");
      }, 800);
    } catch (error) {
      console.error("Failed to delete manual config", error);
      toast.error("Không thể xóa thiết lập. Vui lòng thử lại.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResetForm = () => {
    setConfigId(null);
    setConfigName("");
    setPostQueue([createQueueItem()]);
    setSelectedFanpages([]);
    setSelectedDays(DEFAULT_DAYS);
    setSelectedTimes(["08:00"]);
    setTimeInput("08:00");
    setAutoPostEnabled(true);
    setIncludeContactInfo(true);
    setRepeatQueue(false);
    setMetrics(null);
  };

  // Sắp xếp posts theo thứ tự mặc định (order) - Di chuyển lên trước early return
  const sortedPosts = useMemo(() => {
    return [...postQueue].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [postQueue]);

  if (isLoadingConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin h-12 w-12 rounded-full border-b-2 border-blue-500" />
          <p className="mt-4 text-gray-600 font-medium">
            Đang tải thiết lập thủ công...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          THIẾT LẬP LỊCH ĐĂNG CHO BÀI VIẾT CÓ SẴN
        </h1>

        <ToastContainer position="top-right" autoClose={3000} />
        <PointWarning
          title="Vui lòng nạp thêm điểm để tiếp tục dùng chức năng này nhé ;)"
          userPoint={userPoint}
        />

        <div className="flex flex-col md:flex-row gap-6">
          {/* Cột bên trái - Cài đặt cơ bản */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tên thiết lập
                </label>
                <input
                  type="text"
                  value={configName}
                  onChange={(event) => setConfigName(event.target.value)}
                  placeholder="Ví dụ: Chiến dịch khuyến mãi T10"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="mb-6"></div>
            </div>

            {/* Post Queue Manager - UI tối ưu */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">
                    Quản lý hàng chờ bài viết
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {postQueue.length} bài viết
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Download Template Button */}
                  <button
                    onClick={handleDownloadTemplate}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Tải Template
                  </button>

                  {/* Excel Import Button */}
                  <div className="relative">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleExcelImport(file);
                          e.target.value = ""; // Reset input
                        }
                      }}
                      className="hidden"
                      id="excel-import-input"
                      disabled={isImporting}
                    />
                    <label
                      htmlFor="excel-import-input"
                      className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg border transition-colors cursor-pointer ${
                        isImporting
                          ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                      }`}
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                        />
                      </svg>
                      {isImporting ? "Đang import..." : "Import Excel"}
                    </label>
                  </div>

                  {/* Add Post Button */}
                  <button
                    onClick={handleAddPost}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Thêm bài viết
                  </button>
                </div>
              </div>

              {/* Import Progress Bar */}
              {isImporting && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-700">
                      Đang import file Excel...
                    </span>
                    <span className="text-sm text-blue-600">
                      {importProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Posts List */}
              <div className="space-y-3">
                {sortedPosts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Chưa có bài viết nào trong hàng chờ
                  </div>
                ) : (
                  sortedPosts.map((item, index) => {
                    const isExpanded = expandedPosts.has(item.id);
                    // Tìm index thực tế trong postQueue
                    const realIndex = postQueue.findIndex(
                      (post) => post.id === item.id
                    );

                    return (
                      <div
                        key={item.id}
                        className="border border-gray-200 bg-white hover:border-gray-300 rounded-lg transition-all"
                      >
                        {/* Compact Header */}
                        <div className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900">
                                  Bài #{index + 1}
                                </span>
                                <span
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    STATUS_STYLES[item.status] ||
                                    STATUS_STYLES.pending
                                  }`}
                                >
                                  {item.status === "in_progress"
                                    ? "Đang xử lý"
                                    : item.status === "posted"
                                    ? "Đã đăng"
                                    : item.status === "failed"
                                    ? "Lỗi"
                                    : "Chờ đăng"}
                                </span>
                              </div>

                              <div className="text-sm text-gray-600 max-h-12 overflow-hidden">
                                <p className="break-words whitespace-pre-wrap line-clamp-2">
                                  {item.content || "Không có nội dung"}
                                </p>
                              </div>

                              {item.mediaUrls && (
                                <p className="text-xs text-blue-600 mt-1">
                                  {
                                    item.mediaUrls
                                      .split("\n")
                                      .filter((url) => url.trim()).length
                                  }{" "}
                                  media
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-2">
                              {/* Quick Actions */}
                              <button
                                onClick={() => movePost(realIndex, -1)}
                                disabled={realIndex === 0}
                                className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                title="Lên trên"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 14l5-5 5 5"
                                  />
                                </svg>
                              </button>

                              <button
                                onClick={() => movePost(realIndex, 1)}
                                disabled={realIndex === postQueue.length - 1}
                                className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                title="Xuống dưới"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 10l5 5 5-5"
                                  />
                                </svg>
                              </button>

                              <button
                                onClick={() => togglePostExpansion(item.id)}
                                className="p-1.5 text-gray-400 hover:text-gray-600"
                                title={isExpanded ? "Thu gọn" : "Mở rộng"}
                              >
                                <svg
                                  className="w-4 h-4 transition-all duration-200"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  {isExpanded ? (
                                    // Minus icon khi expanded
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M20 12H4"
                                    />
                                  ) : (
                                    // Plus icon khi collapsed
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 4v16m8-8H4"
                                    />
                                  )}
                                </svg>
                              </button>

                              {/* Duplicate and Delete buttons */}
                              <button
                                onClick={() => handleDuplicatePost(realIndex)}
                                className="px-2 py-1 text-xs text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
                                title="Nhân bản"
                              >
                                Nhân bản
                              </button>
                              <button
                                onClick={() => handleDeletePost(realIndex)}
                                className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                                title="Xóa"
                              >
                                Xóa
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Expanded Content */}
                        {isExpanded && (
                          <div className="border-t border-gray-200 p-4 space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nội dung bài viết
                              </label>
                              <textarea
                                value={item.content}
                                onChange={(event) => {
                                  updateQueueItem(item.id, {
                                    content: event.target.value,
                                  });
                                  // Auto-resize textarea
                                  const textarea = event.target;
                                  textarea.style.height = "auto";
                                  textarea.style.height =
                                    Math.min(textarea.scrollHeight, 300) + "px";
                                }}
                                onInput={(event) => {
                                  // Auto-resize on input
                                  const textarea = event.target;
                                  textarea.style.height = "auto";
                                  textarea.style.height =
                                    Math.min(textarea.scrollHeight, 300) + "px";
                                }}
                                rows={4}
                                style={{
                                  minHeight: "100px",
                                  maxHeight: "300px",
                                  resize: "vertical",
                                }}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 break-words whitespace-pre-wrap overflow-y-auto"
                                placeholder="Nhập nội dung bài viết..."
                              />
                              {item.content && item.content.length > 500 && (
                                <div className="mt-1 text-xs text-gray-500">
                                  {item.content.length} ký tự
                                </div>
                              )}
                            </div>

                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <label className="block text-sm font-medium text-gray-700">
                                  Liên kết media
                                </label>
                                <span className="text-xs text-gray-400">
                                  (Ảnh hoặc tối đa 1 Video)
                                </span>
                              </div>
                              <div className="space-y-2">
                                {(() => {
                                  let urls = item.mediaUrls
                                    .split(/\n|,/)
                                    .map((url) => url.trim());

                                  if (
                                    urls.length === 0 ||
                                    (urls.length === 1 && urls[0] === "")
                                  ) {
                                    urls = [""];
                                  }

                                  return urls.map((url, urlIndex) => (
                                    <div key={urlIndex} className="flex gap-2">
                                      <input
                                        type="url"
                                        value={url}
                                        onChange={(event) => {
                                          const newUrls = [...urls];
                                          newUrls[urlIndex] =
                                            event.target.value.trim();
                                          const filteredUrls = newUrls.filter(
                                            (u) => u.length > 0
                                          );
                                          if (filteredUrls.length === 0) {
                                            filteredUrls.push("");
                                          }
                                          updateQueueItem(item.id, {
                                            mediaUrls: filteredUrls.join("\n"),
                                          });
                                        }}
                                        className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Nhập URL video hoặc ảnh"
                                      />
                                      {urls.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newUrls = urls.filter(
                                              (_, i) => i !== urlIndex
                                            );
                                            const filteredUrls = newUrls.filter(
                                              (u) => u.length > 0
                                            );
                                            if (filteredUrls.length === 0) {
                                              filteredUrls.push("");
                                            }
                                            updateQueueItem(item.id, {
                                              mediaUrls:
                                                filteredUrls.join("\n"),
                                            });
                                          }}
                                          className="px-2 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                        >
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 011-1v1M4 7h16"
                                            />
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                  ));
                                })()}
                                
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const currentUrls = item.mediaUrls
                                        .split(/\n|,/)
                                        .map((url) => url.trim())
                                        .filter((url) => url.length > 0);
                                      const newUrls = [...currentUrls, ""];
                                      updateQueueItem(item.id, {
                                        mediaUrls: newUrls.join("\n"),
                                      });
                                    }}
                                    className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors flex items-center justify-center gap-1.5"
                                  >
                                    <FaImage className="text-xs" />
                                    + Thêm liên kết URL
                                  </button>
                                </div>

                                <div className="mt-3">
                                  <input
                                    type="file"
                                    id={`file-upload-${item.id}`}
                                    onChange={(e) => handleMediaUpload(item.id, e)}
                                    className="hidden"
                                    accept="image/*,video/*"
                                  />
                                  {uploadingPosts[item.id] ? (
                                    <div className="flex flex-col items-center justify-center border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50 text-blue-600 animate-pulse">
                                      <FaSpinner className="animate-spin text-2xl mb-2" />
                                      <span className="text-xs font-medium">Đang tải tệp lên Cloudinary...</span>
                                    </div>
                                  ) : (
                                    <label
                                      htmlFor={`file-upload-${item.id}`}
                                      className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all group"
                                    >
                                      <FaUpload className="text-gray-400 group-hover:text-blue-500 text-xl mb-1.5 transition-colors" />
                                      <span className="text-xs font-medium text-gray-600 group-hover:text-blue-600">Tải ảnh hoặc video từ thiết bị</span>
                                      <span className="text-[10px] text-gray-400 mt-0.5">Hỗ trợ JPG, PNG, WEBP, MP4... (Cloudinary)</span>
                                    </label>
                                  )}
                                </div>
                              </div>

                              {/* Media Previews (Hiển thị thumbnail các file đã chọn/upload) */}
                              {(() => {
                                const urls = item.mediaUrls
                                  .split(/\n|,/)
                                  .map((url) => url.trim())
                                  .filter(Boolean);

                                if (urls.length === 0) return null;

                                return (
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                                    {urls.map((url, urlIndex) => {
                                      const isVideo = /\.(mp4|avi|mov|wmv|flv|mkv|webm|ogv|m4v)$/i.test(url) || url.toLowerCase().includes("/video/");
                                      return (
                                        <div key={urlIndex} className="relative group border border-gray-200 rounded-lg overflow-hidden bg-gray-50 h-24 flex items-center justify-center shadow-sm">
                                          {isVideo ? (
                                            <div className="w-full h-full flex flex-col items-center justify-center p-2 text-gray-500">
                                              <FaFilm className="text-3xl mb-1 text-blue-500" />
                                              <span className="text-[10px] truncate max-w-full text-center px-1">
                                                {url.split('/').pop() || 'Video'}
                                              </span>
                                            </div>
                                          ) : (
                                            <img
                                              src={url}
                                              alt="Preview"
                                              className="w-full h-full object-cover"
                                              onError={(e) => {
                                                e.target.src = 'https://placehold.co/150x150?text=Lỗi+ảnh';
                                              }}
                                            />
                                          )}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newUrls = urls.filter((_, i) => i !== urlIndex);
                                              updateQueueItem(item.id, {
                                                mediaUrls: newUrls.join("\n"),
                                              });
                                            }}
                                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 transition-colors opacity-90 group-hover:scale-105"
                                            title="Xóa media này"
                                          >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}

                              {mediaValidationErrors[item.id] && (
                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                                  ⚠️ {mediaValidationErrors[item.id]}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Add Post Button */}
              <button
                type="button"
                onClick={handleAddPost}
                className="w-full mt-4 border-2 border-dashed border-gray-300 rounded-lg py-4 text-gray-600 hover:border-blue-400 hover:text-blue-600 transition"
              >
                + Thêm bài viết vào hàng chờ
              </button>

              {/* Hướng dẫn upload file lấy link miễn phí */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6 text-sm text-blue-800 leading-relaxed shadow-sm">
                <span className="font-semibold block mb-2 flex items-center gap-1.5 text-base">
                  💡 Mẹo lưu trữ & lấy liên kết media miễn phí:
                </span>
                <p className="mb-3">
                  Nếu bạn chưa có sẵn link trực tuyến cho ảnh/video, bạn có thể tải chúng lên miễn phí tại các dịch vụ lưu trữ sau để lấy liên kết trực tiếp dán vào bài viết:
                </p>
                <div className="flex flex-wrap items-center gap-4 my-3 font-medium bg-white p-2.5 rounded border border-blue-100">
                  <a
                    href="https://imgbb.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5"
                  >
                    🌐 ImgBB (Tải ảnh nhanh)
                  </a>
                  <span className="text-blue-200 hidden sm:inline">|</span>
                  <a
                    href="https://cloudinary.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5"
                  >
                    ☁️ Cloudinary (Ảnh & Video)
                  </a>
                </div>
                <p className="text-xs text-blue-600 italic">
                  * Lưu ý quan trọng: Hãy copy liên kết trực tiếp kết thúc bằng phần mở rộng như <span className="font-mono bg-blue-100 px-1 py-0.5 rounded text-[10px]">.jpg</span>, <span className="font-mono bg-blue-100 px-1 py-0.5 rounded text-[10px]">.png</span>, <span className="font-mono bg-blue-100 px-1 py-0.5 rounded text-[10px]">.mp4</span>... để bài viết được hiển thị và đăng tải chính xác nhất lên các kênh.
                </p>
              </div>
            </div>
          </div>

          {/* Cột bên phải - Cài đặt chi tiết */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Lịch đăng & thiết lập
              </h2>

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-3">
                  Chọn tài khoản để đăng bài
                </label>
                {fanpages.length === 0 ? (
                  <div className="p-4 border border-dashed border-gray-300 rounded-md text-sm text-gray-500 text-center">
                    Kết nối Facebook Fanpage hoặc Zalo OA trong hồ sơ trước khi sử dụng tính năng này.
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="relative" ref={dropdownRef}>
                      <button
                        type="button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="w-full px-4 py-3 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <span className="block truncate">
                          {selectedFanpages.length === 0
                            ? "Chọn tài khoản để đăng bài"
                            : `${selectedFanpages.length} tài khoản${
                                selectedFanpages.length !== 1 ? "s" : ""
                              } đã chọn`}
                        </span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <svg
                            className="w-5 h-5 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      </button>

                      {isDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                          <div className="max-h-48 overflow-y-auto">
                            {fanpages.map((fanpage) => (
                              <div
                                key={getSocialAccountKey(fanpage)}
                                onClick={() => handleToggleFanpage(fanpage)}
                                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${
                                  selectedFanpages.find(
                                    (fp) =>
                                      getSocialAccountKey(fp) ===
                                      getSocialAccountKey(fanpage)
                                  )
                                    ? "bg-blue-50"
                                    : ""
                                }`}
                              >
                                <div className="flex items-center flex-1">
                                  <input
                                    type="checkbox"
                                    checked={selectedFanpages.some(
                                      (fp) =>
                                        getSocialAccountKey(fp) ===
                                        getSocialAccountKey(fanpage)
                                    )}
                                    onChange={() => {}} // Handle by div onClick
                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className="text-left truncate">
                                    {fanpage.name ||
                                      fanpage.page_name ||
                                      fanpage.id}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500 uppercase">
                                    {fanpage.platform === "zalo" ? "Zalo OA" : "Facebook"}
                                  </span>
                                </div>
                                {selectedFanpages.find(
                                  (fp) =>
                                    getSocialAccountKey(fp) ===
                                    getSocialAccountKey(fanpage)
                                ) && (
                                  <span className="text-blue-600 ml-2">✓</span>
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="border-t border-gray-200 px-4 py-3">
                            <button
                              onClick={() => setIsDropdownOpen(false)}
                              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium"
                            >
                              Hoàn tất ({selectedFanpages.length} tài khoản
                              {selectedFanpages.length !== 1 ? "s" : ""} đã
                              chọn)
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {selectedFanpages.length > 0 && (
                      <div className="mt-3">
                        <label className="block text-gray-700 font-medium mb-2">
                          Tài khoản đã chọn ({selectedFanpages.length}):
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {selectedFanpages.map((fanpage) => {
                            const isZalo = (fanpage.platform || "facebook") === "zalo";
                            return (
                              <div
                                key={getSocialAccountKey(fanpage)}
                                className={`inline-flex items-center text-sm px-3 py-1 rounded-full ${
                                  isZalo
                                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                                    : "bg-indigo-100 text-indigo-800"
                                }`}
                              >
                                {isZalo ? (
                                  <img src={zaloIcon} alt="Zalo" className="w-4 h-4 mr-1.5 rounded-sm object-cover" />
                                ) : (
                                  <FaFacebook className="mr-1.5 text-blue-600" />
                                )}
                                <span className="mr-2">
                                  {fanpage.name ||
                                    fanpage.page_name ||
                                    fanpage.id}
                                </span>
                                <button
                                  onClick={() => handleToggleFanpage(fanpage)}
                                  className={`focus:outline-none ${
                                    isZalo
                                      ? "text-blue-500 hover:text-blue-700"
                                      : "text-indigo-600 hover:text-indigo-800"
                                  }`}
                                  title="Bỏ chọn tài khoản này"
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-6">
                <ScheduleModeSelector
                  value={scheduleMode}
                  onChange={setScheduleMode}
                  disabled={isSaving}
                />
              </div>

              {scheduleMode === "weekly" && (
                <div className="mb-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="w-full md:w-1/3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <label className="block text-gray-700 font-medium mb-2">
                        Giờ đăng bài
                      </label>
                      <div className="space-y-3">
                        {selectedTimes.map((time, index) => {
                          const [hours, minutes] = time.split(":");
                          return (
                            <div
                              key={index}
                              className="flex items-center gap-2"
                            >
                              <div className="relative flex-1 flex shadow-sm rounded-md overflow-hidden border border-gray-200">
                                {/* Dropdown giờ */}
                                <div className="relative w-1/2">
                                  <select
                                    value={hours}
                                    onChange={(e) => {
                                      const newTimes = [...selectedTimes];
                                      const newHours = e.target.value;
                                      newTimes[
                                        index
                                      ] = `${newHours}:${minutes}`;
                                      setSelectedTimes(newTimes);
                                    }}
                                    className="w-full h-full px-4 py-2.5 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium"
                                  >
                                    {Array.from({ length: 24 }, (_, i) => {
                                      const hour = i < 10 ? `0${i}` : `${i}`;
                                      return (
                                        <option key={hour} value={hour}>
                                          {hour}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                    <svg
                                      className="w-4 h-4 fill-current"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>

                                {/* Dấu hai chấm */}
                                <div className="flex items-center justify-center bg-gray-50 px-2">
                                  <span className="text-gray-500 font-medium">
                                    :
                                  </span>
                                </div>

                                {/* Dropdown phút */}
                                <div className="relative w-1/2">
                                  <select
                                    value={minutes}
                                    onChange={(e) => {
                                      const newTimes = [...selectedTimes];
                                      const newMinutes = e.target.value;
                                      newTimes[
                                        index
                                      ] = `${hours}:${newMinutes}`;
                                      setSelectedTimes(newTimes);
                                    }}
                                    className="w-full h-full px-4 py-2.5 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium"
                                  >
                                    {(() => {
                                      const isDev = process.env.NODE_ENV === 'development';
                                      const step = isDev ? 1 : 5;
                                      const count = isDev ? 60 : 12;
                                      return Array.from({ length: count }, (_, i) => {
                                        const minute = i * step;
                                        const formattedMinute =
                                          minute < 10
                                            ? `0${minute}`
                                            : `${minute}`;
                                        return (
                                          <option
                                            key={formattedMinute}
                                            value={formattedMinute}
                                          >
                                            {formattedMinute}
                                          </option>
                                        );
                                      });
                                    })()}
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                    <svg
                                      className="w-4 h-4 fill-current"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const newTimes = selectedTimes.filter(
                                    (_, i) => i !== index
                                  );
                                  setSelectedTimes(newTimes);
                                }}
                                className={`p-2 text-white ${
                                  selectedTimes.length > 1
                                    ? "bg-red-500 hover:bg-red-600"
                                    : "bg-gray-300 cursor-not-allowed"
                                } rounded-full transition-colors focus:outline-none ${
                                  selectedTimes.length > 1
                                    ? "focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                    : ""
                                }`}
                                aria-label="Xóa giờ đăng"
                                disabled={selectedTimes.length <= 1}
                                title={
                                  selectedTimes.length <= 1
                                    ? "Phải có ít nhất một giờ đăng bài"
                                    : "Xóa giờ đăng này"
                                }
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 011-1v1M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedTimes([...selectedTimes, "08:00"])
                          }
                          className="w-full px-4 py-2.5 bg-white text-blue-600 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center font-medium shadow-sm"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Thêm giờ đăng
                        </button>
                      </div>
                    </div>

                    <div className="w-full md:w-2/3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <label className="block text-gray-700 font-medium mb-2">
                        Ngày đăng bài
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(daysInVietnamese).map(
                          ([day, label]) => (
                            <label
                              key={day}
                              className={`inline-flex items-center justify-center px-3 py-2 rounded-md cursor-pointer select-none transition-colors duration-200
                            ${
                              selectedDays[day]
                                ? "bg-blue-500 text-white"
                                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                            }`}
                            >
                              <input
                                type="checkbox"
                                className="hidden"
                                checked={selectedDays[day]}
                                onChange={() => handleToggleDay(day)}
                              />
                              <span className="text-sm font-medium">
                                {label}
                              </span>
                            </label>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {scheduleMode === "fixed-dates" && (
                <div className="mb-6">
                  <FixedDateScheduleUI
                    schedules={fixedDateSchedules}
                    onAdd={handleAddFixedDateSchedule}
                    onEdit={handleEditFixedDateSchedule}
                    onDelete={handleDeleteFixedDateSchedule}
                    onToggleCompleted={handleToggleScheduleCompleted}
                  />
                </div>
              )}

              <div className="mb-6">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <label className="text-gray-700 font-medium">
                          Thêm thông tin liên hệ
                        </label>
                        <SettingTooltip
                          label="Thêm thông tin liên hệ"
                          text={CONTACT_INFO_TOOLTIP_TEXT}
                        />
                      </div>
                      <div className="relative inline-block align-middle select-none shrink-0">
                        <input
                          type="checkbox"
                          name="contactInfo"
                          id="contactInfo"
                          checked={includeContactInfo}
                          onChange={(event) =>
                            setIncludeContactInfo(event.target.checked)
                          }
                          className="hidden"
                        />
                        <label
                          htmlFor="contactInfo"
                          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                          style={{
                            backgroundColor: includeContactInfo
                              ? "#3b82f6"
                              : "#e5e7eb",
                          }}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                              includeContactInfo
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                    <div className="flex items-center justify-between">
                      <label className="text-gray-700 font-medium">
                        Lặp lại hàng chờ khi hết bài
                      </label>
                      <div className="relative inline-block align-middle select-none">
                        <input
                          type="checkbox"
                          name="repeatQueue"
                          id="repeatQueue"
                          checked={repeatQueue}
                          onChange={(event) =>
                            setRepeatQueue(event.target.checked)
                          }
                          className="hidden"
                        />
                        <label
                          htmlFor="repeatQueue"
                          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                          style={{
                            backgroundColor: repeatQueue
                              ? "#3b82f6"
                              : "#e5e7eb",
                          }}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                              repeatQueue ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <label className="block text-gray-700 font-medium">
                  Tự động viết và đăng bài
                </label>
                <div className="relative inline-block align-middle select-none">
                  <input
                    type="checkbox"
                    name="autoPost"
                    id="autoPost"
                    checked={autoPostEnabled}
                    onChange={(e) => setAutoPostEnabled(e.target.checked)}
                    className="hidden"
                  />
                  <label
                    htmlFor="autoPost"
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                    style={{
                      backgroundColor: autoPostEnabled ? "#3b82f6" : "#e5e7eb",
                    }}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                        autoPostEnabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </label>
                </div>
              </div>
              {autoPostEnabled ? (
                <p className="mt-2 text-sm text-gray-600">
                  Hệ thống sẽ tự động viết và đăng bài theo thời gian đã cài đặt
                </p>
              ) : (
                <p className="mt-2 text-sm text-gray-600">
                  Hiện tại tính năng tự động viết và đăng bài tự động đã tắt.
                  Vui lòng bật để sử dụng
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleSaveConfig}
                  disabled={isSaving}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {isSaving
                    ? "Đang lưu..."
                    : configId
                    ? "Cập nhật"
                    : "Lưu thiết lập"}
                </button>
                {configId && (
                  <button
                    type="button"
                    onClick={handleDeleteConfig}
                    disabled={isDeleting}
                    className="px-6 py-3 border border-rose-300 text-rose-600 rounded-lg hover:bg-rose-50 disabled:opacity-50 transition"
                  >
                    {isDeleting ? "Đang xóa..." : "Xóa"}
                  </button>
                )}
              </div>
            </div>

            {/* Liên kết bài đăng gần đây */}
            {configId && historyLinks.length > 0 && configFromState && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Bài đăng gần đây
                </h3>
                <div className="space-y-3">
                  {historyLinks.slice(0, 5).map((entry, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="text-sm text-gray-600">
                          {formatTimestamp(entry.timestamp)}
                        </p>
                      </div>
                      <a
                        href={entry.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Xem bài đăng
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Thống kê hàng chờ */}
            {configId && (
              <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      Thống kê hàng chờ
                    </h3>
                    <p className="text-sm text-gray-600">
                      Theo dõi trạng thái và hiệu suất đăng bài
                    </p>
                  </div>
                  {metrics && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        Tổng kết tháng này
                      </p>
                      <p className="text-sm font-medium">
                        <span className="text-emerald-600">
                          {metrics.posted || 0}
                        </span>
                        {" / "}
                        <span className="text-gray-600">
                          {(metrics.posted || 0) + (metrics.failed || 0)}
                        </span>
                        {" bài"}
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg text-center border border-gray-200">
                    <p className="text-2xl font-bold text-gray-800 mb-1">
                      {queueStats.total}
                    </p>
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                      Tổng số
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-1 mt-2">
                      <div
                        className="bg-gray-400 h-1 rounded-full"
                        style={{ width: "100%" }}
                      ></div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-lg text-center border border-amber-200">
                    <p className="text-2xl font-bold text-amber-700 mb-1">
                      {queueStats.pending}
                    </p>
                    <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                      Chờ đăng
                    </p>
                    <div className="w-full bg-amber-200 rounded-full h-1 mt-2">
                      <div
                        className="bg-amber-400 h-1 rounded-full"
                        style={{
                          width:
                            queueStats.total > 0
                              ? `${
                                  (queueStats.pending / queueStats.total) * 100
                                }%`
                              : "0%",
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 rounded-lg text-center border border-emerald-200">
                    <p className="text-2xl font-bold text-emerald-700 mb-1">
                      {queueStats.posted}
                    </p>
                    <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">
                      Hoàn tất
                    </p>
                    <div className="w-full bg-emerald-200 rounded-full h-1 mt-2">
                      <div
                        className="bg-emerald-400 h-1 rounded-full"
                        style={{
                          width:
                            queueStats.total > 0
                              ? `${
                                  (queueStats.posted / queueStats.total) * 100
                                }%`
                              : "0%",
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-rose-50 to-rose-100 p-4 rounded-lg text-center border border-rose-200">
                    <p className="text-2xl font-bold text-rose-700 mb-1">
                      {queueStats.failed}
                    </p>
                    <p className="text-xs font-medium text-rose-700 uppercase tracking-wide">
                      Thất bại
                    </p>
                    <div className="w-full bg-rose-200 rounded-full h-1 mt-2">
                      <div
                        className="bg-rose-400 h-1 rounded-full"
                        style={{
                          width:
                            queueStats.total > 0
                              ? `${
                                  (queueStats.failed / queueStats.total) * 100
                                }%`
                              : "0%",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Progress overview */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Tiến độ xử lý:</span>
                    <span className="font-medium">
                      {queueStats.total > 0
                        ? Math.round(
                            ((queueStats.posted + queueStats.failed) /
                              queueStats.total) *
                              100
                          )
                        : 0}
                      % hoàn thành
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-gradient-to-r from-emerald-400 to-emerald-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width:
                          queueStats.total > 0
                            ? `${
                                ((queueStats.posted + queueStats.failed) /
                                  queueStats.total) *
                                100
                              }%`
                            : "0%",
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualPostGenerator;
